/**
 * Le pattern du Montage « Couper dans le rythme » — SPEC-UI §5.3 et §4.2.
 *
 * ⚠️ **Transitoire, et assumé comme tel.** Le pattern est un objet **serveur** (SPEC-API §6.2,
 * tâche M2.8) : c'est lui qui doit le seeder et le valider, sinon la triche est triviale. En
 * bac à sable il n'y a pas de serveur, donc on le génère ici — mais **avec le RNG du moteur**
 * (`draw`, purpose `atelier_pattern`) et la graine dérivée que `generateAtelierPattern` rend
 * déjà (`seed:turn:stage`). Conséquence voulue : **rejouer le même tour redonne le même
 * pattern** (anti re-roll, PLAN §6) — c'est vérifiable en jouant.
 *
 * Les constantes ci-dessous sont la table §4.2 à **E1**, et rien d'autre : ce sont elles que
 * Mathieu ajustera « à la main en jouant » (avenant A-3, tâches M2.8). Le scaling par palier
 * vient du moteur (`exigenceAtelier`), jamais d'une copie locale.
 */

import {
  carresDuPattern,
  choisirPatternMontage,
  draw,
  exigenceAtelier,
  generateAtelierPattern,
  pistesJouables,
  type GameState,
  type MontagePatternsFile,
  type VideoFormat,
} from "@maxime/shared";

import type { PatternMontageApi, VarianteMontageApi } from "../../api/atelier";

import type { Piste } from "./scoring.js";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes de base (SPEC-UI §4.2, colonne E1) — les seules valeurs à régler
// ─────────────────────────────────────────────────────────────────────────────

/** Tempo de base. Le scaling par palier le multiplie par `tempo_mult` (1,08^k). */
export const BPM_BASE = 90;
/** Fenêtre rythmique TOTALE de base, en ms. `window_mult` la resserre (0,90^k). */
export const FENETRE_BASE_MS = 180;
/**
 * ⟦C54 · A-171⟧ Les coups d'une manche par niveau d'exigence E0..E5, par format : des PALIERS (8, 12, 16, 20, 24,
 * 30). Jumelle de `cuts_short` / `cuts_longue` de `content/atelier.json` (le bac à sable ne lit pas les valeurs
 * d'un niveau ailleurs que dans l'état). La manche dure `DUREE_MS`, toujours : plus de coups, c'est une
 * subdivision plus serrée, jamais une manche plus longue.
 */
export const COUPS_PAR_NIVEAU = {
  short: [8, 12, 16, 20, 24, 30],
  longue: [12, 16, 20, 24, 30, 30],
} as const;
/** Durée FIXE de la manche, en ms (constante non scalée, §4.2 ; ⟦C54⟧ : quel que soit le nombre de coups). */
export const DUREE_MS = { short: 20_000, longue: 30_000 } as const;
/** Pré-affichage d'un marqueur avant son instant — 2 500 avec *Sound design qui claque*. */
export const PREVIEW_MS = 1200;
export const PREVIEW_MS_SOUND_DESIGN = 2500;
/** Décompte avant le début de la piste : 3 temps (§5.3 pas 1). */
export const TEMPS_DE_DECOMPTE = 3;

/**
 * **Mode doux** (E0, sous 100 abonnés — SPEC-UI §4.1) : fenêtres ×1,30, tempo ×0,90,
 * cibles ×0,80.
 *
 * ⚠️ **Divergence n°1 (complétée ici)** : `exigenceAtelier()` (moteur) n'applique le mode
 * doux **qu'aux fenêtres** ; le tempo ×0,90 et les cibles ×0,80 de §4.1 n'y sont pas. On les
 * complète côté pattern — c'est bien sa juridiction (§4.1 : « le serveur calcule les
 * paramètres effectifs et les envoie »), et le moteur n'est pas touché. Résultat vérifié :
 * à E0 on retrouve **exactement** la colonne E0 de la table §4.2 (81 BPM, 234 ms, 8 cuts).
 *
 * ⚠️ **Divergence n°2 (SIGNALÉE, PAS TRANCHÉE — conflit entre deux specs)** : le `k` du
 * moteur et celui de SPEC-UI n'ont pas la même origine.
 * - SPEC-MOTEUR §9.2 / `exigenceAtelier` : `k` = **nombre de paliers franchis** (0 sous 100
 *   abonnés, 1 à partir de 100, 2 à partir de 1 000…).
 * - SPEC-UI §4.1 : « les paramètres de base sont définis à **E1** ; pour E ≥ 1, avec
 *   **k = E − 1** ».
 *
 * Les deux sont donc décalés d'un cran dès E1 : le moteur donne `1,08^1` là où la table §4.2
 * attend `1,08^0`. Conséquence concrète et visible en jouant : à 100 abonnés on obtient la
 * colonne **E2** de §4.2 (97 BPM, 162 ms, 19 cuts) au lieu de la colonne E1 (90 / 180 / 16).
 *
 * **Ce fichier suit le moteur** — « le moteur fait foi » (brief de mission), et c'est lui qui
 * est implémenté et testé. Corriger reviendrait à écrire `Math.pow(1.08, max(0, k - 1))` ici,
 * c'est-à-dire à arbitrer seul entre deux specs : CLAUDE.md l'interdit (« en cas de conflit
 * chiffré entre specs : signaler, ne pas trancher seul »). À trancher par Mathieu, en M2.8
 * quand le pattern passera côté serveur.
 */
export const MODE_DOUX_TEMPO = 0.9;
// ⟦C54⟧ Les cibles du mode doux (×0,80) ne sont plus une règle de trois : les coups du niveau sont des paliers
// (`COUPS_PAR_NIVEAU`, 8 à E0), et le souffle de fin de piste est dans le fichier de patterns (`queue_ms`).

/** Variante « mode posé » de SPEC-UI §7.4 (prefers-reduced-motion) : W = 800, inter-cut fixe. */
export const POSE_FENETRE_MS = 800;
export const POSE_INTERCUT_MS = 1500;

export interface Cut {
  /** Instant absolu depuis le début de la piste, en ms. */
  atMs: number;
  /** Piste multicam à frapper — absente en mono-piste. */
  track?: Piste;
  /**
   * ⟦C47 · A-168 §2⟧ Pour le RENDU seulement : `double` (le second d'une paire, un temps après le
   * premier) ou `contretemps` (entre deux temps). Le barème ne le lit pas, un cut est un cut, à
   * `atMs`.
   */
  type?: "double" | "contretemps";
}

/** ⟦C47 · A-168 §2⟧ La variante servie par le serveur (`classique` = le jeu d'avant). */
export type VarianteMontage = VarianteMontageApi;

/**
 * ⟦C47⟧ Ce que l'écran dit d'une variante avant de la jouer. Le texte dit la règle, jamais où
 * elle tombe : les marqueurs, eux, se distinguent à l'écran (forme et couleur) quand ils arrivent.
 */
export const CONSIGNES: Record<VarianteMontage, { badge: string; consigne: string; court: string } | null> = {
  classique: null,
  // ⟦C57 · lot 8⟧ `court` : la phrase de la consigne à l'écran (12 mots au plus). `consigne`, la phrase
  // d'origine, reste : elle se lit derrière « Les dessous » de l'écran de consigne.
  doubles: {
    badge: "Cuts doublés",
    consigne:
      "Certains cuts arrivent par deux, à un temps d'écart : frappe le premier, puis le second, sans souffler.",
    court: "Cuts par deux : frappe le premier, puis le second.",
  },
  contretemps: {
    badge: "Contretemps",
    consigne:
      "Un passage de la piste tombe entre les temps : ne suis plus la pulsation, suis les losanges.",
    court: "Entre les temps : suis les losanges.",
  },
};

/** Bonus de fenêtre en multicam : « en compensation, fenêtre ×1,15 » (§5.3 pas 3). */
export const MULTICAM_FENETRE = 1.15;

// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-59⟧ « Le repérage » — la phase de dérushage qui précède la piste
// ─────────────────────────────────────────────────────────────────────────────
//
// Jumeau **non autoritatif** du bloc `REPERAGE_*` de `server/src/atelier/constantes.ts`. Le
// verbe est *repérer* : avant que la piste défile, on pose sur elle ses propres marqueurs —
// moins nombreux que les cuts, donc il faut choisir lesquels on annonce.

/** Combien de marqueurs, par format — toujours **moins que de cuts**. */
export const REPERAGE_MARQUEURS = { short: 2, longue: 3 } as const;
export const REPERAGE_CHRONO_BASE_MS = 12_000;
export const REPERAGE_RAYON_BASE_MS = 2_400;

export interface Reperage {
  chronoMs: number;
  marqueurs: number;
  /** Rayon d'influence d'un marqueur, en ms de piste. */
  rayonMs: number;
  /** Où chaque marqueur attend, en fraction de piste. */
  departs: number[];
}

export interface PatternMontage {
  /** ⟦C47⟧ La variante servie (toujours `classique` en bac à sable : le serveur tire les autres). */
  variante: VarianteMontage;
  bpm: number;
  /** Période d'un temps, en ms. */
  beatMs: number;
  durationMs: number;
  /** Fenêtre rythmique TOTALE effective (le barème §5.3 raisonne en W/2 et W/4). */
  windowMs: number;
  leadInMs: number;
  previewMs: number;
  cuts: Cut[];
  /** 1 sans *Multicam*, 2 avec, 3 à E5 (§5.3 pas 3). */
  trackCount: 1 | 2 | 3;
  /** ⟦A-59⟧ La phase de dérushage, ou `null` — le seul interrupteur de l'état neutre. */
  reperage: Reperage | null;
  /** ⟦C38 · A-161 §3⟧ La piste musicale tirée, ou `null` (silence : mode posé). */
  piste: string | null;
  /** ⟦C50⟧ Toujours 1 : la musique n'est plus étirée. */
  playbackRate: number;
  /** ⟦C50 · A-169 §2⟧ L'instant du FICHIER qui est le zéro du jeu (`atMs = 0`). 0 hors musique. */
  origineMs: number;
  /** ⟦C50⟧ La grille du morceau est-elle assez fiable pour afficher un métronome et un BPM ? */
  pulsation: boolean;
  /** Le score sera plafonné à cette valeur (85 en tolérance — SPEC-UI §7.3). */
  scoreCap: number;
  /** Variante posée active (§7.4) : pas de défilement, un cut à la fois. */
  pose: boolean;
  /** Exigence E affichée à l'intro. */
  k: number;
}

export interface OptionsPattern {
  state: GameState;
  format: VideoFormat;
  seed: string;
  /** `prefers-reduced-motion` : bascule en mode posé (§7.4). */
  pose: boolean;
  /** Assist *Sound design qui claque* — la piste s'affiche plus tôt. */
  previewLong?: boolean;
  /** Assist *Jump cut sauvage* — fenêtre +15 %. */
  fenetreBonus?: number;
  /** Assist *Chapitrage* — tempo perçu −10 %. */
  tempoMult?: number;
  /** Assist *Multicam* — les cuts se répartissent sur 2 pistes (3 à E5). */
  multicam?: boolean;
  /** ⟦A-59⟧ *Chapitrage* : la phase « Le repérage » précède la piste. */
  phaseReperage?: boolean;
  /**
   * ⟦C50 · A-169 §2⟧ Les patterns pré-calculés sur l'audio (`content/montage-patterns.json`, validé par
   * `MontagePatternsFile`) : en bac à sable, le client TIRE dedans exactement comme le serveur, il ne calcule
   * aucun carré. Requis hors mode posé (le mode posé n'a ni musique ni grille).
   */
  patterns?: MontagePatternsFile | null;
}

/**
 * Génère le pattern. Déterministe : `(seed, turn, "montage")` ⇒ mêmes cuts, toujours.
 */
export function genererPattern(options: OptionsPattern): PatternMontage {
  const { state, format, seed, pose } = options;
  const exigence = exigenceAtelier(state);
  const modeDoux = state.subscribers < 100;

  const durationMs = DUREE_MS[format];
  // Le multicam n'existe pas en mode posé : les cuts s'y présentent déjà un par un.
  const trackCount: 1 | 2 | 3 =
    pose || !options.multicam ? 1 : exigence.k >= 5 ? 3 : 2;
  const windowMs = pose
    ? POSE_FENETRE_MS
    : Math.round(
        FENETRE_BASE_MS *
          exigence.window_mult *
          (options.fenetreBonus ?? 1) *
          (trackCount > 1 ? MULTICAM_FENETRE : 1),
      );

  const graine = generateAtelierPattern(state, "montage", seed).seed_component;
  // ⟦C54⟧ *Chapitrage* (« tempo perçu −10 % ») retire 10 % des coups : dans une manche de durée fixe, un tempo plus lent.
  const vises = nombreDeCuts(format, exigence.k) * (options.tempoMult ?? 1);

  let cuts: Cut[];
  let piste: string | null = null;
  let origineMs = 0;
  let pulsation = false;
  let bpm: number;
  let beatMs: number;
  let leadInMs: number;
  if (pose) {
    // Mode posé (§7.4) : les cuts se présentent un par un, à intervalle fixe, sans musique ni grille.
    bpm = Math.round(
      BPM_BASE * exigence.tempo_mult * (modeDoux ? MODE_DOUX_TEMPO : 1) * (options.tempoMult ?? 1),
    );
    beatMs = 60_000 / bpm;
    leadInMs = 1200;
    cuts = cutsPoses(format, exigence.k);
  } else {
    // ⟦C50⟧ Bac à sable : le jumeau non autoritatif du tirage serveur (`server/src/atelier/patterns.ts`),
    // dans le MÊME fichier de patterns et par la MÊME fonction de choix (`choisirPatternMontage`, `@maxime/shared`).
    // Aucun carré n'est calculé ici : ils sont lus dans le pattern tiré. La variante reste `classique` (le
    // serveur tire les autres) ; en compte, ce bloc ne joue pas (`depuisServeur`).
    if (!options.patterns) {
      throw new Error("genererPattern : le fichier de patterns du Montage est requis hors mode posé");
    }
    const choix = choisirPatternMontage(options.patterns, {
      format,
      vises,
      variante: "classique",
      indexPiste: indexPisteBacASable(seed, state.turn, pistesJouables(options.patterns).length),
      u: draw(graine, 0, "atelier_pattern", 701),
      uDensite: draw(graine, 0, "atelier_pattern", 702),
    });
    if (!choix) throw new Error(`aucun pattern du Montage pour ${vises} cuts (${format})`);
    piste = choix.piste.id;
    origineMs = choix.pattern.origineMs;
    pulsation = choix.piste.pulsation;
    bpm = Math.round(choix.piste.bpm);
    beatMs = choix.piste.beatMs;
    leadInMs = choix.piste.decompteMs;
    cuts = carresDuPattern(choix.piste, choix.pattern).map(({ atMs, type }): Cut => ({ atMs, ...(type ? { type } : {}) }));
  }
  // Alternance des pistes **seedée** (§5.3 pas 3) : la même graine rend le même enchaînement.
  if (trackCount > 1) {
    const pistes: Piste[] = trackCount === 3 ? ["A", "B", "C"] : ["A", "B"];
    cuts.forEach((cut, i) => {
      cut.track = pistes[Math.floor(draw(graine, 0, "atelier_pattern", 500 + i) * trackCount)] ?? "A";
    });
  }

  // ⟦C54 · A-171⟧ **La manche dure sa durée, toujours** : 8 coups ou 30, la musique ne s'allonge pas. Le retour de
  // playtest du 14/08 (« les 8 cuts doivent être avant la fin du compteur », « si tu finis, le jeu s'arrête ») reste
  // tenu autrement : chaque pattern pose son dernier coup dans la dernière seconde et demie avant les 750 ms de
  // souffle (`fenetre_dernier_ms` et `queue_ms` du fichier de patterns). Le mode posé garde sa règle d'avant.

  return {
    variante: "classique",
    bpm,
    beatMs,
    durationMs: pose ? (cuts.at(-1)?.atMs ?? 0) + POSE_INTERCUT_MS : durationMs,
    windowMs,
    leadInMs,
    previewMs: options.previewLong ? PREVIEW_MS_SOUND_DESIGN : PREVIEW_MS,
    cuts,
    trackCount,
    // ⟦A-59⟧ Aucune graine n'est consommée ici, présente ou absente : le repérage annote des
    // cuts déjà tirés, il n'invente rien. L'état neutre ⟦A-10⟧ est structurel, pas négocié.
    reperage: options.phaseReperage
      ? reperageLocal(format, exigence.window_mult, exigence.tempo_mult)
      : null,
    scoreCap: exigence.score_cap,
    pose,
    k: exigence.k,
    piste,
    playbackRate: 1,
    origineMs,
    pulsation,
  };
}

/**
 * ⟦C38⟧ L'indice du morceau, bac à sable : même garantie que le serveur (aucune répétition entre deux tours
 * d'affilée), sur `draw()` plutôt que sur le hash SHA-256 du serveur — « les deux suites ne coïncident pas,
 * c'est sans importance » (rng.ts serveur) hors de la partie comptée, où c'est `depuisServeur` qui fait foi.
 */
function indexPisteBacASable(seed: string, turn: number, n: number): number {
  if (n <= 1) return 0;
  let precedent = -1;
  let index = 0;
  for (let t = 0; t <= Math.max(0, Math.trunc(turn)); t++) {
    const bornes = precedent < 0 ? n : n - 1;
    const brut = Math.min(bornes - 1, Math.floor(draw(seed, t, "atelier_pattern", 700) * bornes));
    index = precedent < 0 || brut < precedent ? brut : brut + 1;
    precedent = index;
  }
  return index;
}

/** Le repérage du bac à sable — jumeau de `GenerateurPatterns#reperage`. */
function reperageLocal(format: VideoFormat, windowMult: number, tempoMult: number): Reperage {
  const combien = REPERAGE_MARQUEURS[format];
  return {
    chronoMs: Math.max(5_000, Math.round(REPERAGE_CHRONO_BASE_MS / tempoMult)),
    marqueurs: combien,
    rayonMs: Math.max(900, Math.round(REPERAGE_RAYON_BASE_MS * windowMult)),
    departs: Array.from({ length: combien }, (_, i) => (i + 1) / (combien + 1)),
  };
}

/**
 * Le pattern **servi par le serveur**, traduit dans la forme que la piste joue déjà.
 *
 * Traduction pure : les cuts, la fenêtre, le tempo et le nombre de pistes arrivent **calculés**
 * — scaling par palier, assists et tolérance compris (§4.1). Les deux divergences signalées en
 * tête de ce fichier (mode doux appliqué au tempo et aux cibles, `k` décalé d'un cran entre
 * SPEC-MOTEUR et SPEC-UI) cessent d'exister en mode compte : c'est la lecture du serveur qui
 * fait foi, et elle est la même pour tout le monde.
 */
/**
 * ⚠️ **Le `scoreCap` du fil n'est PAS celui du barème d'écran.**
 *
 * L'enveloppe du serveur porte `exigenceAtelier(état, étape).score_cap`, qui **fusionne** deux
 * bornes : la tolérance d'accessibilité (85) et le **plafond de compétence** ⟦A-9⟧ de la
 * branche (55 au socle). Or le moteur applique déjà le second à la publication
 * (`scoresEffectifs`), et l'écran de l'Atelier le montre à part — « 92 · plafonné à 45, ton
 * niveau ». Le laisser raboter le score du mini-jeu, ce serait l'appliquer **deux fois** et
 * saboter le geste, ce que l'avenant interdit explicitement : « le joueur voit son vrai geste,
 * et voit ce que son niveau en retient ».
 *
 * Le barème local ne retient donc que la borne qui ne dépend de rien, lue du moteur —
 * exactement ce que fait la génération en bac à sable. Le serveur note pareil : son
 * `validate.ts` ne connaît que `TOLERANCE_SCORE_CAP`.
 */
export function depuisServeur(api: PatternMontageApi, state: GameState): PatternMontage {
  return {
    variante: api.variante ?? "classique",
    bpm: api.params.bpm,
    beatMs: api.params.beatMs,
    durationMs: api.params.durationMs,
    windowMs: api.params.windowMs,
    leadInMs: api.params.leadInMs,
    previewMs: api.params.previewMs,
    // ⟦C47⟧ `type` ne sert qu'au rendu (doublé, contretemps) : le barème ne le lit pas.
    cuts: api.params.cuts.map((c) => ({
      atMs: c.atMs,
      track: c.track,
      ...(c.type ? { type: c.type } : {}),
    })),
    trackCount: api.params.trackCount,
    reperage: api.params.reperage
      ? { ...api.params.reperage, departs: [...api.params.reperage.departs] }
      : null,
    scoreCap: exigenceAtelier(state).score_cap,
    // ⟦A-83⟧ Le serveur génère la variante posée quand on la lui demande (SPEC-UI §7.4) : les
    // cuts à intervalle fixe, l'anneau de 800 ms et le lead-in sont déjà dans les params.
    pose: api.reducedMotion,
    k: api.tier,
    // ⟦C38 · A-161 §3⟧ Déjà tirée côté serveur — le client la joue, il ne la choisit plus.
    piste: api.params.piste,
    playbackRate: 1,
    // ⟦C50⟧ Absents d'un serveur d'avant C50 : 0 (hors musique) et pulsation affichée (l'ancien jeu).
    origineMs: api.params.origineMs ?? 0,
    pulsation: api.params.pulsation ?? true,
  };
}

/** ⟦C54⟧ Les coups du niveau d'exigence `k` (E0..E5) : l'échelle de paliers, jamais une règle de trois. */
function nombreDeCuts(format: VideoFormat, k: number): number {
  const colonne = COUPS_PAR_NIVEAU[format];
  return colonne[Math.max(0, Math.min(colonne.length - 1, Math.trunc(k)))]!;
}

/** Mode posé (§7.4) : les cuts se présentent un par un, à intervalle fixe. */
function cutsPoses(format: VideoFormat, k: number): Cut[] {
  const combien = nombreDeCuts(format, k);
  return Array.from({ length: combien }, (_, i) => ({
    atMs: (i + 1) * POSE_INTERCUT_MS,
  }));
}
